package org.solyton.solawi.bid.application.ui.page.application.management


import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import org.evoleq.compose.Markup
import org.evoleq.compose.guard.data.isLoading
import org.evoleq.compose.guard.data.onEmpty
import org.evoleq.compose.guard.data.onNullLaunch
import org.evoleq.compose.guard.data.withLoading
import org.evoleq.compose.layout.Horizontal
import org.evoleq.compose.routing.navigate
import org.evoleq.device.data.mediaType
import org.evoleq.language.component
import org.evoleq.language.description
import org.evoleq.language.title
import org.evoleq.language.tooltip
import org.evoleq.math.emit
import org.evoleq.math.map
import org.evoleq.math.times
import org.evoleq.optics.lens.DeepSearch
import org.evoleq.optics.lens.FirstBy
import org.evoleq.optics.prism.Either
import org.evoleq.optics.storage.Read
import org.evoleq.optics.storage.Storage
import org.evoleq.optics.storage.dispatch
import org.evoleq.optics.transform.firstByOrNull
import org.evoleq.optics.transform.times
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Text
import org.solyton.solawi.bid.application.data.*
import org.solyton.solawi.bid.application.data.env.i18nEnvironment
import org.solyton.solawi.bid.application.data.transform.application.management.applicationManagementModule
import org.solyton.solawi.bid.application.data.transform.user.userIso
import org.solyton.solawi.bid.application.ui.effect.LaunchComponentLookup
import org.solyton.solawi.bid.application.ui.page.application.i18n.ApplicationLangComponent
import org.solyton.solawi.bid.application.ui.page.application.style.actionsWrapperStyle
import org.solyton.solawi.bid.application.ui.page.application.style.listItemWrapperStyle
import org.solyton.solawi.bid.module.application.action.fixApplicationRelatedContexts
import org.solyton.solawi.bid.module.application.action.readApplications
import org.solyton.solawi.bid.module.application.action.readPersonalApplicationContextRelations
import org.solyton.solawi.bid.module.application.action.updateStandardApplicationContext
import org.solyton.solawi.bid.module.application.component.modal.showManageDefaultContextModal
import org.solyton.solawi.bid.module.application.data.ApplicationId
import org.solyton.solawi.bid.module.application.data.application.modules
import org.solyton.solawi.bid.module.application.data.management.applicationManagementActions
import org.solyton.solawi.bid.module.application.data.management.availableApplications
// import org.solyton.solawi.bid.module.application.data.management.availablePermissions
import org.solyton.solawi.bid.module.application.data.management.personalApplicationContextRelations
import org.solyton.solawi.bid.module.application.i18n.Component
import org.solyton.solawi.bid.module.application.i18n.Component.editContext
import org.solyton.solawi.bid.module.application.i18n.Component.fixApplicationContext
import org.solyton.solawi.bid.module.application.i18n.application
import org.solyton.solawi.bid.module.application.i18n.module
import org.solyton.solawi.bid.module.control.button.ArrowUpButton
import org.solyton.solawi.bid.module.control.button.DetailsButton
import org.solyton.solawi.bid.module.control.button.EditButton
import org.solyton.solawi.bid.module.control.button.ScrewDriverWrenchButton
import org.solyton.solawi.bid.module.dialog.component.WarningSymbol
import org.solyton.solawi.bid.module.dialog.component.showDialogModal
import org.solyton.solawi.bid.module.dialog.i18n.dialogModalTexts
import org.solyton.solawi.bid.module.i18n.data.language
import org.solyton.solawi.bid.module.i18n.guard.onMissing
import org.solyton.solawi.bid.module.list.component.*
import org.solyton.solawi.bid.module.list.style.defaultListStyles
import org.solyton.solawi.bid.module.loading.component.Loading
import org.solyton.solawi.bid.module.page.component.Page
import org.solyton.solawi.bid.module.permission.data.ContextId
import org.solyton.solawi.bid.module.permissions.data.contexts
import org.solyton.solawi.bid.module.permissions.service.contextFromPath
import org.solyton.solawi.bid.module.style.modal.commonModalStyles
import org.solyton.solawi.bid.module.style.page.PageTitle
import org.solyton.solawi.bid.module.style.page.SubTitle
import org.solyton.solawi.bid.module.style.page.verticalPageStyle
import org.solyton.solawi.bid.module.style.wrap.Wrap
import org.solyton.solawi.bid.module.user.action.permission.readUserPermissionsAction
import org.solyton.solawi.bid.module.user.data.userActions


@Markup
@Composable
@Suppress("FunctionName", "CognitiveComplexMethod")
fun ApplicationPage(storage: Storage<Application>, applicationId: String) = withLoading(
    isLoading = isLoading(
        onEmpty(storage * applicationManagementModule * availableApplications.get) {
            CoroutineScope(Job()).launch {
                (storage * applicationManagementModule * applicationManagementActions).dispatch(
                    readApplications
                )
            }
        },
        // Note: personalApplicationContextRelations is deliberately NOT part of this loading
        // gate (unlike availableApplications above). onEmpty() can't tell "not fetched yet"
        // apart from "fetched, genuinely empty" - and for an application owner/admin (who isn't
        // personally registered for any application) that list IS legitimately always empty, so
        // gating on it here would leave this page stuck in Loading() forever for such users. It
        // is instead fetched once below, and the page already tolerates it being empty (see
        // defaultContext's use of firstOrNull).
        onNullLaunch(
            storage * availablePermissions * contextFromPath("APPLICATION"),
        ) {
            CoroutineScope(Job()).launch { (storage * userIso * userActions).dispatch(readUserPermissionsAction()) }
        },
        onMissing(
            ApplicationLangComponent.ApplicationPage,
            storage * i18N.get
        ) {
            LaunchComponentLookup(
                langComponent = ApplicationLangComponent.ApplicationPage,
                environment = storage * environment * i18nEnvironment,
                i18n = (storage * i18N)
            )
        },
        *(storage * applicationManagementModule * availableApplications).read().map {
            onMissing(
                ApplicationLangComponent.ApplicationDetails(it.name),
                storage * i18N.get
            ){
                LaunchComponentLookup(
                    langComponent = ApplicationLangComponent.ApplicationDetails(it.name),
                    environment = storage * environment * i18nEnvironment,
                    i18n = (storage * i18N)
                )
            }
        }.toBooleanArray()
    ),
    onLoading = {Loading()}
){
    // Data
    val device = storage * deviceData * mediaType.get

    val application = storage * applicationManagementModule * availableApplications * FirstBy { it.id == applicationId }
    // todo:dev are the personalApplicationRelations the right choice here?
    val contextRelations = (storage * applicationManagementModule * personalApplicationContextRelations).read()
    val defaultContextId = contextRelations.firstOrNull{ relation -> relation.relatedId == applicationId }
        ?.contextId?:"NO_DEFAULT_CONTEXT_ID"
    val defaultContextPrism = (storage * availablePermissions * contexts).firstByOrNull()
    val defaultContext = defaultContextPrism.match{ it.contextId == defaultContextId }

    // Texts
    val applicationTexts = storage * i18N * language * Component.base
    val texts = storage * i18N * language * component(ApplicationLangComponent.ApplicationPage)
    val pageTitle = texts * title
    val allApps = texts * Component.actions * Component.navToAppManPage
    val listOfModules = texts * Component.listOfModules
    val defaultContextTexts = texts * Component.defaultContext


    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        launch {
            (storage * applicationManagementModule * applicationManagementActions).dispatch(
                readPersonalApplicationContextRelations
            )
        }
    }

    Page({verticalPageStyle() }) {
        Wrap {
            Horizontal(styles = {
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                width(100.percent)
            }) {
                PageTitle( pageTitle map{ "$it: ${(applicationTexts * application(application.read().name) * title).emit()}"})
                Horizontal {
                    ArrowUpButton(
                        Color.black,
                        Color.white,
                        allApps * tooltip,
                        device,
                        false,
                        dataId = "application.page.back-button",
                    ) {
                        navigate("/app/management")
                    }
                }
            }
            SubTitle(applicationTexts * application(application.read().name) * description)
        }

            ListWrapper{
                TitleWrapper {
                    Title { H3{ Text((listOfModules * title).emit()) }}
                }
                HeaderWrapper {
                    Header {
                        HeaderCell(listOfModules * Component.headers * Component.module * title) { width(40.percent) }
                    }
                }
                ListItemsIndexed(application * modules) { index, module ->
                    ListItemWrapper({
                        listItemWrapperStyle(this, index)
                    }) {
                        DataWrapper {
                            TextCell(
                                applicationTexts *
                                        module(application.read().name, module.name) *
                                        title
                            ) { width(40.percent) }

                        }
                        ActionsWrapper({
                            actionsWrapperStyle(this)
                        }) {
                            DetailsButton(
                                Color.black,
                                Color.white,
                                listOfModules * Component.actions * Component.showDetails * tooltip,
                                device,
                                dataId = "application.page.module.${module.name}.details-button",
                            ) {
                                CoroutineScope(Job()).launch {
                                    navigate(
                                        "/app/management/application/${applicationId}/module/${module.id}"
                                    )
                                }
                            }
                        }
                    }
                }
            }

            ListWrapper {
                TitleWrapper({
                    defaultListStyles.titleWrapper(this)
                    justifyContent(JustifyContent.SpaceBetween)
                }) {
                    Title { H3{ Text((defaultContextTexts * title).emit()) } }
                    Horizontal {

                        ScrewDriverWrenchButton(
                            Color.black,
                            Color.white,
                            defaultContextTexts * Component.actions * fixApplicationContext * tooltip,
                            device,
                            dataId = "application.page.default-context.fix-button",
                        ){
                            scope.launch {
                                storage * applicationManagementModule * applicationManagementActions dispatch fixApplicationRelatedContexts(
                                    ApplicationId(applicationId)
                                )
                            }
                        }

                        EditButton(
                            Color.black,
                            Color.white,
                            defaultContextTexts * Component.actions * editContext * tooltip,
                            device,
                            dataId = "application.page.default-context.edit-button",
                        ) {
                            defaultContext mapRight { context ->

                                (storage * modals).showManageDefaultContextModal(
                                    texts = { dialogModalTexts("message") },
                                    device = device,
                                    styles = { commonModalStyles(device) },
                                    storage = storage * applicationManagementModule,
                                    contextId = ContextId(context.contextId),
                                    cancel = {},
                                ) {
                                    (storage * modals).showDialogModal(
                                        texts = dialogModalTexts("Confirm"),
                                        device = device,
                                        symbol = {WarningSymbol(deviceType = device.emit())},
                                        onCancel = {},
                                    ) {
                                        val modifiedContext = Read(storage * availablePermissions * contexts * DeepSearch { it.contextId == context.contextId }).emit()
                                        val oldRoles = context.roles.associateBy({ it.roleName }) {
                                            it.rights.map { r -> r.rightName }.toSet()
                                        }
                                        val newRoles = modifiedContext.roles.associateBy({ it.roleName }) {
                                            it.rights.map { r -> r.rightName }.toSet()
                                        }
                                        val changed = oldRoles != newRoles
                                        if (changed) scope.launch {
                                            (storage * applicationManagementModule * applicationManagementActions) dispatch updateStandardApplicationContext(
                                                applicationId = ApplicationId(applicationId),
                                                defaultContextId = ContextId(context.contextId),
                                                roles = modifiedContext.roles,
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                HeaderWrapper {
                    Header {
                        HeaderCell(defaultContextTexts * Component.headers * Component.role * title) { width(40.percent) }
                        HeaderCell(defaultContextTexts * Component.headers * Component.rights * title) { width(40.percent) }
                    }
                }
                if(defaultContext is Either.Right) {
                    ListItemsIndexed(defaultContext.value.roles.sortedBy { it.roleName }) { index, role ->
                        ListItemWrapper({
                            listItemWrapperStyle(this, index)
                        }) {
                            DataWrapper {
                                TextCell(role.roleName) { width(40.percent) }
                                Div({style {
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Row)
                                    flexWrap(FlexWrap.Wrap)
                                    width(60.percent)
                                    flexShrink(0)
                                }}) { role.rights.sortedBy { it.rightName }.forEach { right ->
                                    TextCell(right.rightName,right.rightName){
                                        flexWrap(FlexWrap.Wrap)
                                        width(31.percent)
                                        padding(1.percent)
                                        flexShrink(0)
                                        overflow("hidden")
                                    }
                                } }
                            }
                            ActionsWrapper({
                                actionsWrapperStyle(this)
                            }) {
                                EditButton(
                                    Color.black,
                                    Color.white,
                                    defaultContextTexts * Component.actions * Component.editRole * tooltip,
                                    device,
                                    dataId = "application.page.role.${role.roleName}.edit-button",
                                ) {
                                    (storage * modals).showDialogModal(
                                        texts = dialogModalTexts("Not Implemented: Manage Associated Rights"),
                                        device = device,
                                        dataId = "application.page.edit-rights.not-implemented",
                                    ) {
                                        CoroutineScope(Job()).launch {}
                                    }
                                }
                            }
                        }
                    }
                } else {
                    TextCell("No default context available") { width(100.percent); color(Color.red) }
                }
            }
        }
    }

